import { useState } from 'react';
import { toast } from 'sonner';
import { commentSchema } from '@auditwire/contracts';
import { Button, Textarea } from '@/shared/ui';
import { toMessage } from '@/shared/lib/repository/errors';
import { useAddComment } from '../api/useComments';

interface CommentComposerProps {
  articleId: string;
  parentId?: string | null;
  placeholder?: string;
  onDone?: () => void;
}

export function CommentComposer({ articleId, parentId = null, placeholder, onDone }: CommentComposerProps) {
  const [body, setBody] = useState('');
  const addComment = useAddComment(articleId);

  const submit = async () => {
    const parsed = commentSchema.safeParse({ body });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? 'Write something first');
      return;
    }
    try {
      await addComment.mutateAsync({ body: parsed.data.body, parentId });
      setBody('');
      onDone?.();
    } catch (err) {
      toast.error(toMessage(err, 'Could not post your comment'));
    }
  };

  return (
    <div className="space-y-2">
      <Textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={parentId ? 2 : 3}
        maxLength={2000}
        placeholder={placeholder ?? 'Add to the discussion…'}
      />
      <div className="flex items-center justify-end gap-2">
        {onDone ? (
          <button
            type="button"
            onClick={onDone}
            className="rounded-full px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            Cancel
          </button>
        ) : null}
        <Button size="sm" onClick={() => void submit()} disabled={addComment.isPending || !body.trim()}>
          {addComment.isPending ? 'Posting…' : 'Post'}
        </Button>
      </div>
    </div>
  );
}
